// The org's model fallbacks (`models.fallbacks`, FALLBACK-1) as the Fallbacks card sees them: one row
// per model the org has a chain for, the models each select may offer, the edits, and where a 422
// lands. Pure and DOM-free (tests/unit/models/fallbacks-model.test.ts). The offer rule is the API's
// `fallback_not_allowed` (api/internal/domain/settings/fallbacks.go): a fallback must be offered to
// every agent that is offered the model it replaces, and the global list must allow it — so the
// selects never offer a model the save would refuse.
import type { ApiError } from "../../api/errors";
import { getPath, isObject, setPath, type Json, type JsonObject } from "../../settings/inherit";
import { HARNESSES } from "./model";
import { globalAllows, globalOf, nameOf, offeredModels, type CatalogModel } from "./provider-model";

export const FALLBACKS_PATH = "models.fallbacks";
/** The schema's `to` maxItems. */
export const MAX_TARGETS = 3;
export const FALLBACK_RULE_CODES = ["fallback_not_allowed", "fallback_invalid"] as const;

interface Chain {
  from: string;
  to: string[];
}

export interface FallbackTarget {
  model: string;
  /** What a person reads; never the family id. */
  name: string;
}

export interface FallbackRow {
  from: string;
  fromName: string;
  targets: FallbackTarget[];
  /** "When Opus 5.5 is unavailable, use Sonnet 5.5, then Fable 5.1." */
  sentence: string;
}

/** The document's chains; one with no list (a document that predates the member) is none. */
function chainsOf(effective: Json | undefined): Chain[] {
  const raw = getPath(effective, FALLBACKS_PATH);
  if (!Array.isArray(raw)) return [];
  return raw.filter(isObject).map((entry) => ({
    from: typeof entry["from"] === "string" ? entry["from"] : "",
    to: Array.isArray(entry["to"]) ? (entry["to"] as unknown[]).filter((m): m is string => typeof m === "string") : [],
  }));
}

function withChains(document: JsonObject, chains: Chain[]): JsonObject {
  return setPath(document, FALLBACKS_PATH, chains.filter((c) => c.to.length > 0) as unknown as Json) as JsonObject;
}

export function fallbackRows(effective: Json | undefined, catalog: readonly CatalogModel[]): FallbackRow[] {
  return chainsOf(effective).map((chain) => {
    const fromName = nameOf(catalog, chain.from);
    const targets = chain.to.map((model) => ({ model, name: nameOf(catalog, model) }));
    return { from: chain.from, fromName, targets, sentence: sentenceOf(fromName, targets) };
  });
}

function sentenceOf(fromName: string, targets: readonly FallbackTarget[]): string {
  const [first, ...rest] = targets;
  if (!first) return `When ${fromName} is unavailable, nothing is tried instead.`;
  return `When ${fromName} is unavailable, use ${[first.name, ...rest.map((t) => `then ${t.name}`)].join(", ")}.`;
}

/** The models an agent is offered: the ones a fallback chain may start from. */
function offeredAnywhere(effective: Json | undefined, catalog: readonly CatalogModel[]): CatalogModel[] {
  return catalog.filter((m) => HARNESSES.some((h) => offeredModels(effective, h, catalog).some((o) => o.model === m.model)));
}

/** What the row's first select offers: an allowed model without a chain of its own (`keep`: the row's own). */
export function fromChoices(effective: Json | undefined, catalog: readonly CatalogModel[], keep?: string): CatalogModel[] {
  const taken = new Set(chainsOf(effective).map((c) => c.from));
  return offeredAnywhere(effective, catalog).filter((m) => m.model === keep || !taken.has(m.model));
}

/**
 * What a fallback select offers for `from`: the global list allows it, every agent offered `from` is
 * offered it too, and it is neither `from` nor another slot of the chain (`slot`: the select's own).
 */
export function targetChoices(effective: Json | undefined, catalog: readonly CatalogModel[], from: string, chain: readonly string[], slot?: number): CatalogModel[] {
  const global = globalOf(effective);
  const users = HARNESSES.filter((h) => offeredModels(effective, h, catalog).some((m) => m.model === from));
  const others = new Set(chain.filter((_, i) => i !== slot));
  return catalog.filter(
    (m) =>
      m.model !== from &&
      !others.has(m.model) &&
      globalAllows(global, m.provider, m.model) &&
      users.every((h) => offeredModels(effective, h, catalog).some((o) => o.model === m.model)),
  );
}

// ------------------------------------------------------------------------------------- the edits

/** A new row: the first allowed model without a chain, with its first allowed fallback; unchanged when there is none. */
export function addRow(document: JsonObject, catalog: readonly CatalogModel[]): JsonObject {
  const from = fromChoices(document, catalog).find((m) => targetChoices(document, catalog, m.model, []).length > 0);
  if (!from) return document;
  const to = targetChoices(document, catalog, from.model, [])[0]!;
  return withChains(document, [...chainsOf(document), { from: from.model, to: [to.model] }]);
}

export function removeRow(document: JsonObject, row: number): JsonObject {
  const chains = chainsOf(document);
  if (!chains[row]) return document;
  return withChains(document, chains.filter((_, i) => i !== row));
}

/**
 * The model a row replaces. A fallback that would now be the model itself is dropped; when that
 * leaves none, the first allowed fallback is chosen, so changing a select never removes a row.
 */
export function setFrom(document: JsonObject, row: number, from: string, catalog: readonly CatalogModel[]): JsonObject {
  return editChain(document, row, (chain) => {
    const to = chain.to.filter((m) => m !== from);
    const first = targetChoices(document, catalog, from, to)[0];
    return { from, to: to.length > 0 || !first ? to : [first.model] };
  });
}

export function setTarget(document: JsonObject, row: number, slot: number, model: string): JsonObject {
  return editChain(document, row, (chain) => ({ ...chain, to: chain.to.map((m, i) => (i === slot ? model : m)) }));
}

/** One more fallback, the first allowed one; unchanged when the chain is full or nothing is left to offer. */
export function addTarget(document: JsonObject, row: number, catalog: readonly CatalogModel[]): JsonObject {
  const chain = chainsOf(document)[row];
  if (!chain || chain.to.length >= MAX_TARGETS) return document;
  const next = targetChoices(document, catalog, chain.from, chain.to)[0];
  if (!next) return document;
  return editChain(document, row, (c) => ({ ...c, to: [...c.to, next.model] }));
}

/** A fallback removed; the row goes with its last one, since a chain needs at least one model. */
export function removeTarget(document: JsonObject, row: number, slot: number): JsonObject {
  return editChain(document, row, (chain) => ({ ...chain, to: chain.to.filter((_, i) => i !== slot) }));
}

/** A fallback moved one place earlier (-1) or later (+1); an edge move changes nothing. */
export function moveTarget(document: JsonObject, row: number, slot: number, by: -1 | 1): JsonObject {
  const chain = chainsOf(document)[row];
  const to = slot + by;
  if (!chain || to < 0 || to >= chain.to.length) return document;
  return editChain(document, row, (c) => {
    const next = [...c.to];
    [next[slot], next[to]] = [next[to]!, next[slot]!];
    return { ...c, to: next };
  });
}

function editChain(document: JsonObject, row: number, edit: (chain: Chain) => Chain): JsonObject {
  const chains = chainsOf(document);
  const chain = chains[row];
  if (!chain) return document;
  return withChains(document, chains.map((c, i) => (i === row ? edit(chain) : c)));
}

// ------------------------------------------------------------------------------------ the refusal

export interface RowError {
  message: string;
  /** The fallback slot the refusal names; null when it is the row's model to replace. */
  target: number | null;
}

const POINTER = /^\/models\/fallbacks\/(\d+)(?:\/(from|to)(?:\/(\d+))?)?$/;

/** Where each fallback refusal belongs: on its row, and the slot it names. The message is the server's. */
export function fallbackErrorsFrom(error: ApiError): Record<number, RowError> {
  const out: Record<number, RowError> = {};
  if (!(FALLBACK_RULE_CODES as readonly string[]).includes(error.code)) return out;
  for (const detail of error.details) {
    const match = POINTER.exec(detail.field ?? "");
    if (!match) continue;
    const row = Number(match[1]);
    out[row] ??= { message: detail.message ?? error.message, target: match[3] !== undefined ? Number(match[3]) : null };
  }
  return out;
}
